import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { formatINR, formatDateTime } from "@/lib/format";
import { Loader2, Search, Banknote, ShieldCheck, AlertTriangle, CheckCircle2, Landmark } from "lucide-react";
import { toast } from "sonner";

const PStatus = ({ s }) => {
  const map = {
    Pending: "bg-amber-50 text-amber-700", Processing: "bg-sky-50 text-sky-700",
    Success: "bg-emerald-50 text-emerald-700", Failed: "bg-rose-50 text-rose-700",
    Reversed: "bg-rose-50 text-rose-700", Blocked: "bg-slate-100 text-slate-600", Cancelled: "bg-slate-100 text-slate-600",
  };
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>;
};

export default function AdminDisburse() {
  const [tab, setTab] = useState("approved");
  const [gw, setGw] = useState({ configured: false, ready: false, mode: "sandbox", spec_confirmed: false });
  const [rows, setRows] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pstatus, setPstatus] = useState("ALL");
  const [sel, setSel] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [g, a, p] = await Promise.all([
        api.get("/admin/payout/config"),
        api.get("/admin/disburse/approved", { params: { q: q || undefined } }),
        api.get("/admin/payouts", { params: { status: pstatus, q: q || undefined } }),
      ]);
      setGw(g.data); setRows(a.data); setPayouts(p.data);
    } finally { setLoading(false); }
  }, [q, pstatus]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const confirmPayout = async () => {
    if (!ack || !sel) return;
    setBusy(true);
    try {
      await api.post(`/admin/applications/${sel.application_id}/payout`, { confirm: true, amount: sel.disbursement_amount });
      toast.success("Payout initiated");
      setSel(null); setAck(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not initiate payout");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-disburse-page">
      <div className="flex items-center gap-2">
        <Banknote className="h-6 w-6 text-brand" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Approved Loans / Disburse</h1>
          <p className="text-muted-foreground text-sm">Review approved loans and disburse via BladePay Bank Payout.</p>
        </div>
      </div>

      <Card className={`p-4 ip-card-shadow border-2 ${gw.ready ? "border-emerald-200" : "border-amber-200"}`} data-testid="payout-gateway-status">
        <div className="flex items-start gap-3">
          {gw.ready ? <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" /> : <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />}
          <div>
            <p className="font-semibold">BladePay Payout: {gw.ready ? "Ready" : gw.configured ? "Configured — spec not confirmed" : "Not configured"} · Mode: <span className="uppercase">{gw.mode}</span></p>
            <p className="text-sm text-muted-foreground">{gw.ready
              ? "Payouts will be sent to BladePay and confirmed via webhook."
              : "Add BLADEPAY_* secrets and set BLADEPAY_SPEC_CONFIRMED=true (after verifying BladePay's official API spec). Until then, no real transfer is sent."}</p>
          </div>
        </div>
      </Card>

      <div className="flex gap-2">
        <Button variant={tab === "approved" ? "default" : "outline"} size="sm" onClick={() => setTab("approved")} data-testid="tab-approved">Approved Loans</Button>
        <Button variant={tab === "history" ? "default" : "outline"} size="sm" onClick={() => setTab("history")} data-testid="tab-history">Payout History</Button>
      </div>

      <Card className="p-5 ip-card-shadow">
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="disburse-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer name, email or application number" className="pl-9" />
          </div>
          {tab === "history" && (
            <Select value={pstatus} onValueChange={setPstatus}>
              <SelectTrigger className="sm:w-44" data-testid="payout-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>{["ALL", "Pending", "Processing", "Success", "Failed", "Blocked"].map((s) => <SelectItem key={s} value={s}>{s === "ALL" ? "All statuses" : s}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>

        <div className="overflow-x-auto">
          {tab === "approved" ? (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2.5 pr-4 font-medium">Application</th>
                <th className="py-2.5 pr-4 font-medium">Customer</th>
                <th className="py-2.5 pr-4 font-medium">Approved</th>
                <th className="py-2.5 pr-4 font-medium hidden md:table-cell">Disburse</th>
                <th className="py-2.5 pr-4 font-medium hidden lg:table-cell">Beneficiary</th>
                <th className="py-2.5 pr-4 font-medium">Action</th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No approved loans awaiting disbursement.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.offer_id} className="border-b border-border/60" data-testid={`disburse-row-${r.reference_number}`}>
                    <td className="py-3 pr-4 font-mono font-semibold">{r.reference_number}</td>
                    <td className="py-3 pr-4"><div className="font-medium">{r.customer_name}</div><div className="text-xs text-muted-foreground">{r.customer_email}</div></td>
                    <td className="py-3 pr-4 font-mono">{formatINR(r.approved_amount)}</td>
                    <td className="py-3 pr-4 font-mono hidden md:table-cell">{formatINR(r.disbursement_amount)}</td>
                    <td className="py-3 pr-4 hidden lg:table-cell text-xs"><div>{r.beneficiary.account_holder}</div><div className="text-muted-foreground font-mono">{r.beneficiary.account_number_masked} · {r.beneficiary.ifsc}</div></td>
                    <td className="py-3 pr-4">
                      {r.payout ? <PStatus s={r.payout.status} /> : r.ready ? (
                        <Button size="sm" onClick={() => { setSel(r); setAck(false); }} data-testid={`disburse-button-${r.reference_number}`}>Disburse</Button>
                      ) : <span className="text-xs text-muted-foreground">Awaiting customer confirm</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-2.5 pr-4 font-medium">Reference</th>
                <th className="py-2.5 pr-4 font-medium">Customer</th>
                <th className="py-2.5 pr-4 font-medium">Amount</th>
                <th className="py-2.5 pr-4 font-medium hidden lg:table-cell">Txn Ref</th>
                <th className="py-2.5 pr-4 font-medium hidden sm:table-cell">Created</th>
                <th className="py-2.5 pr-4 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {payouts.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No payouts yet.</td></tr>}
                {payouts.map((p) => (
                  <tr key={p.transaction_reference} className="border-b border-border/60" data-testid={`payout-row-${p.transaction_reference}`}>
                    <td className="py-3 pr-4 font-mono font-semibold">{p.reference_number}</td>
                    <td className="py-3 pr-4">{p.customer_name}</td>
                    <td className="py-3 pr-4 font-mono">{formatINR(p.amount)}</td>
                    <td className="py-3 pr-4 font-mono text-xs hidden lg:table-cell">{p.transaction_reference}</td>
                    <td className="py-3 pr-4 text-muted-foreground hidden sm:table-cell">{formatDateTime(p.created_at)}</td>
                    <td className="py-3 pr-4"><PStatus s={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="disburse-confirm-dialog">
          <DialogHeader><DialogTitle>Confirm loan disbursement</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 border border-border p-4 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Application</span><span className="font-mono font-semibold">{sel.reference_number}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span>{sel.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Disbursement amount</span><span className="font-mono font-bold text-brand">{formatINR(sel.disbursement_amount)}</span></div>
              </div>
              <div className="rounded-lg border border-border p-4 text-sm">
                <p className="flex items-center gap-1.5 font-semibold mb-1"><Landmark className="h-4 w-4 text-brand" /> Beneficiary</p>
                <p>{sel.beneficiary.account_holder}</p>
                <p className="text-muted-foreground font-mono">{sel.beneficiary.bank_name} · {sel.beneficiary.account_number_masked} · {sel.beneficiary.ifsc}</p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={ack} onCheckedChange={setAck} className="mt-0.5" data-testid="disburse-ack-checkbox" />
                <span className="text-sm text-muted-foreground">I have reviewed the amount and beneficiary and authorize this transfer. This action initiates a real bank payout.</span>
              </label>
              <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
                <p>Funds move only after BladePay confirms the payout via verified webhook. Status will show as Processing until then.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSel(null)}>Cancel</Button>
            <Button onClick={confirmPayout} disabled={!ack || busy} className="bg-gold hover:bg-gold-dark text-white" data-testid="disburse-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Banknote className="h-4 w-4 mr-2" />} Confirm & Disburse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
